import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { authAPI, affiliateAuthAPI } from '../../utils/api';
import { useTheme } from '../../contexts/ThemeContext';
import AuthFloatingControls from './AuthFloatingControls.jsx';

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { isDark } = useTheme();
  const isAffiliate = pathname.startsWith('/affiliate');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loginPath = isAffiliate ? '/affiliate/login' : '/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const api = isAffiliate ? affiliateAuthAPI : authAPI;
      const result = await api.forgotPassword(email.trim());
      setSuccess(result.message || t('auth.resetRequestSent'));
    } catch (err) {
      setError(err.message || t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AuthFloatingControls />
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center -mt-24">
            <img
              src={isDark ? '/images/Logo-dark.png' : '/images/Logo-light.png'}
              alt="Logo"
              className="w-48 h-48 object-contain mx-auto"
            />
          </div>
          <div className="glass-card">
            <h1 className="text-2xl font-bold mb-2 text-center">{t('auth.forgotPassword')}</h1>
            <p className="text-sm text-center mb-6" style={{ color: 'var(--text-secondary)' }}>
              {t('auth.forgotPasswordDescription')}
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl flex items-center space-x-2" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 rounded-xl flex items-center space-x-2" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">{success}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">{t('auth.email')}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="glass-input"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button type="submit" disabled={loading} className="glass-button w-full py-3 font-semibold disabled:opacity-50">
                {loading ? t('auth.sending') : t('auth.sendResetLink')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to={loginPath} className="font-medium hover:underline" style={{ color: 'var(--accent-primary)' }}>
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
